import { useLocalStore, useObserver } from 'mobx-react-lite';
import React, { HTMLAttributes } from 'react';
import 'react-sliding-pane/dist/react-sliding-pane.css';
import styled from 'styled-components/macro';
import rotate from '../keyframes/rotate';
import { useAddressSelectStore } from '../models/AddressSelect';
import { CartItemHelper } from '../models/CartItem';
import { useMst } from '../models/WithWineState';
import {
  CartItemOptionQueryStringModel,
  CartItemQueryStringModel,
} from '../types/CartItemQueryStringModel';
import { CartDispatchData } from '../types/EventDataProps';
import { TypeShippingRateType } from '../types/TypeShippingRateType';
import { base64ToBase64URL } from '../utils/base64ToBase64URL';
import {
  createProductData,
  dispatchCheckoutListData,
} from '../utils/dispatchHelper';
import AddressContainer from './AddressContainer';
import Button from './Button';
import CartItem from './CartItem';
import CartV2 from './CartV2';
import Modal from './Modal';

const UnstyledCart: React.FC<HTMLAttributes<HTMLDivElement>> = ({
  className,
}) => {
  const stateTree = useMst();
  const addressSelectStore = useAddressSelectStore();

  const state = useLocalStore(() => ({
    onStateChange(e: React.ChangeEvent<HTMLSelectElement>) {
      stateTree.session?.setDeliveryState(e.target.value);
      stateTree.session?.calculateCart();
    },

    get isAuth() {
      return stateTree.session?.authenticated ?? false;
    },

    get showVersion2() {
      return stateTree.widgets.cart.openVersion2 ?? '';
    },

    get dispatchEventData() {
      const cartItems: CartDispatchData[] = [];

      stateTree.session?.cart.forEach((cartItem) => {
        const cart = {
          product: createProductData(cartItem.product),
          quantity: cartItem.quantity,
        };
        cartItems.push(cart);
      });

      return {
        user: {
          id: stateTree.session?.user?.id,
          name: stateTree.session?.user?.name,
          sessionId: stateTree.session?.authenticated
            ? stateTree.session?.user?.id
            : stateTree.session?.id,
        },
        cart: cartItems,
      };
    },

    get state() {
      return stateTree.session?.deliveryFields?.state ?? '';
    },

    get shippingFee() {
      const fee = stateTree.session?.shippingFee;
      return typeof fee === 'number' ? +fee.toFixed(2) : undefined;
    },

    get isOpen() {
      if (stateTree.widgets.cart.open) {
        this.items.forEach((item) => {
          const parent = document.getElementById(item.id.toString());

          if (parent) {
            const btn = parent.querySelector('#btn-close') as HTMLElement;
            btn.click();
          }
        });
      }
      return stateTree.widgets.cart.open;
    },

    get items() {
      return stateTree.session?.items || [];
    },

    get errorMessage() {
      return stateTree.session?.calculatePriceError;
    },

    get totalPrice() {
      if (stateTree.session?.calculatedCartPrice) {
        return +stateTree.session?.calculatedCartPrice;
      } else {
        return (
          +state.items.reduce((sum: number, cartItem) => {
            return sum + (cartItem.price || 0);
          }, 0) + (state.shippingFee ?? 0)
        );
      }
    },

    get totalTaxAdded() {
      return stateTree.session?.totalTaxAdded ?? 0;
    },

    get addedTaxCodeDescription() {
      return stateTree.session?.addedTaxCodeDescription || 'Sales Tax';
    },

    get totalItemCount() {
      return stateTree.session?.getCartQuantityByProductGroup(null) ?? 0;
    },

    get allItemsStockAvailable() {
      return state.items.every((item) => item.stockAvailable);
    },

    get ableToCheckout() {
      return (
        stateTree.session?.calculatePriceState === 'idle' &&
        stateTree.session?.calculatedCartPrice &&
        !stateTree.session?.calculatePriceError &&
        stateTree.session?.getCartQuantityByProductGroup(null) > 0 &&
        state.allItemsStockAvailable
      );
    },

    get isStateShipping() {
      const brand = Array.from(stateTree.data.brands.values())[0];
      return brand.shippingRateType === TypeShippingRateType.ByState;
    },

    clearCart() {
      stateTree.session?.clearCart();
    },

    hide() {
      stateTree.widgets.cart.hide();
    },

    checkout() {
      if (!state.ableToCheckout) {
        return;
      }

      dispatchCheckoutListData(state.dispatchEventData);

      if (state.items) {
        const queryStringCartModel = state.items.map((item) => {
          const cartItem: CartItemQueryStringModel = {
            P: item.id,
            Q: item.value,
          };
          if (item.selectedOptions && item.selectedOptions.length) {
            const numberOfOptions = CartItemHelper.getOptionQuantity(
              item.minimumUnitPurchase,
              item.value,
            );
            cartItem.O = item.selectedOptions?.map((so) => {
              const option: CartItemOptionQueryStringModel = {
                Q: numberOfOptions,
                I: 0,
              };
              if (so.productId) {
                option.P = so.productId;
              } else if (so.name) {
                option.N = so.name;
              }

              return option;
            });
          }
          return cartItem;
        });
        const queryStringCartJson = JSON.stringify(queryStringCartModel);
        const base64EncodedData = btoa(queryStringCartJson);
        const cartUrlParam = base64ToBase64URL(base64EncodedData);
        const sessionKey = state.isAuth
          ? ``
          : `&sessionKey=${stateTree.session?.id}`;
        window.location.href = `/user/checkout?state=${state.state}&cart=${cartUrlParam}${sessionKey}`;
      }
    },
  }));

  return useObserver(() => {
    return !state.showVersion2 ? (
      <Modal
        className={'ww-widget ww-cart ' + className}
        open={state.isOpen}
        onBackdropClick={state.hide}
        header={<h3 className="header">Cart</h3>}
        // eslint-disable-next-line react/no-children-prop
        children={
          <div
            className={
              'cart-items cart-price-state-' +
              (stateTree.session?.calculatePriceState ?? '')
            }
          >
            {state.items.map((val) => (
              <CartItem
                key={'ci_' + val.id}
                {...val}
                calculatingState={stateTree.session?.calculatePriceState}
              />
            ))}
            {state.items.length === 0 && (
              <p className="cart-empty">Your cart is empty</p>
            )}
            {state.items.length !== 0 && (
              <>
                <div className="shipping-state sub-total">
                  {addressSelectStore.hasAddressFieldSelection ? (
                    <div>
                      <label>Shipping to</label>
                      <AddressContainer selectmessage="Select location for shipping" />
                    </div>
                  ) : (
                    <label>Shipping</label>
                  )}
                  <div className="shipping-fee sub-total-amount">
                    {typeof state.shippingFee === 'number'
                      ? `$${state.shippingFee?.toFixed(2)}`
                      : '$-.--'}
                  </div>
                </div>
                {!state.errorMessage &&
                  state.totalItemCount > 0 &&
                  state.totalTaxAdded !== 0 && (
                    <div className="added-tax sub-total">
                      <label>{state.addedTaxCodeDescription}</label>
                      <div className="added-tax-amount sub-total-amount">
                        {`$${state.totalTaxAdded.toFixed(2)}`}
                      </div>
                    </div>
                  )}
                {!state.errorMessage ? (
                  state.totalItemCount > 0 && (
                    <div className="total">
                      <h3>Total</h3>
                      <h3 className="amount">
                        ${state.totalPrice.toFixed(2)}
                        {typeof state.shippingFee === 'number' ? (
                          <></>
                        ) : (
                          <span className="amount-addon">Plus Shipping</span>
                        )}
                      </h3>
                    </div>
                  )
                ) : (
                  <div className="error-message">{state.errorMessage}</div>
                )}
              </>
            )}
          </div>
        }
        footer={
          <div className="actions">
            <Button onClick={() => state.clearCart()}>Empty Cart</Button>
            <Button onClick={state.hide}>
              Continue<span className="hide-sm"> Shopping</span>
            </Button>
            <Button
              primary
              solid
              onClick={state.checkout}
              disabled={!state.ableToCheckout}
            >
              Checkout{' '}
              <div className="checkout-icon">
                {stateTree.session?.calculatePriceState === 'pending' ? (
                  <div className="spinner">
                    <i className="icon-spinner" />
                  </div>
                ) : (
                  <i className="icon-right-open" />
                )}
              </div>
            </Button>
          </div>
        }
      />
    ) : (
      <CartV2></CartV2>
    );
  });
};

const Cart = styled(UnstyledCart)`
  .header {
    margin: 0;
    font-size: 1.17em;
  }

  .hide-sm {
    display: none;
  }

  .actions {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: stretch;
  }

  .actions > * {
    flex: 1;
    margin-right: 1em;
  }

  .actions > *:last-child {
    margin-right: 0;
  }

  .cart-empty {
    text-align: center;
    color: var(--gray-3);
    margin: 0;
    padding: 1.5em;
    background-color: var(--white);
  }

  .sub-total {
    padding: 0.25em 1em;
    background-color: var(--white);
    display: flex;
    align-items: center;
  }

  .sub-total > label,
  .sub-total > div {
    flex: 1 1 auto;
  }

  .sub-total label {
    font-size: 1em;
  }

  .sub-total .sub-total-amount {
    display: flex;
    flex: 0 0 100px;
    justify-content: flex-end;
    font-size: 1em;
  }

  .sub-total > label > span {
    margin: 0.25em 1em 0.25em 0;
    display: inline-block;
  }

  .cart-state {
    padding: 0.5em 1em;
    border-color: var(--gray-2);
    color: var(--gray-5);
    font-family: inherit;
    font-size: inherit;
  }

  .cart-items {
    display: flex;
    flex-direction: column;
  }

  .cart-items .item {
    margin-bottom: 0.15em;
  }

  .cart-items .shipping,
  .cart-items .total {
    background-color: var(--white);
    padding: 1em;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }

  .cart-items .error-message {
    background-color: var(--warn-color);
    padding: 1em;
    display: flex;
    flex-direction: row;
    color: var(--white);
  }

  .cart-items .shipping h3,
  .cart-items .total h3 {
    margin: 0;
    font-size: 1.17em;
  }
  .cart-items .total .amount {
    text-align: right;
  }
  .cart-items .total .amount .amount-addon {
    display: block;
    font-weight: bold;
  }

  .spinner {
    overflow: hidden;
    width: 1em;
    height: 1em;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin: 0;
    animation: ${rotate} 2s linear infinite;
    opacity: 0.5;
  }

  .checkout-icon {
    display: none;
    margin-left: 0.25em;
  }

  .cart-price-state-pending .amount,
  .cart-price-state-pending .sub-total-amount {
    color: var(--gray-1);
    background-color: var(--gray-1);
  }

  @media (min-width: 569px) {
    .hide-sm {
      display: initial;
    }

    .checkout-icon {
      display: inline-block;
    }

    .actions {
      justify-content: flex-end;
      align-items: stretch;
    }

    .actions > * {
      flex: none;
    }
  }
`;

export default Cart;
